import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Autocomplete, Box, Button, CircularProgress, Divider, InputAdornment,
  LinearProgress, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getSdTickets, downloadSdReport } from "../../../../store/support-desk/actions";
import { getDistricts } from "../../../../store/posts/actions";

const T = {
  navy: "#0A1E3D", orange: "#F16522", orangeGrad: "linear-gradient(100deg,#F16522 0%,#F7941E 100%)",
  bg: "#EEF2F8", card: "#fff", ink: "#101B30", ink2: "#4A5A76", ink3: "#8494AD", line: "#E3E9F2",
};

const STATUS_MAP = {
  submitted: { label: "Submitted",        color: "#2B5BC4", bg: "#EAF0FB" },
  review:    { label: "Under review",     color: "#B7791F", bg: "#FEF3E0" },
  district:  { label: "Sent to district", color: "#6B4EE0", bg: "#EEEAFB" },
  progress:  { label: "In progress",      color: "#1673C4", bg: "#E4F2FF" },
  resolved:  { label: "Resolved",         color: "#12885A", bg: "#E3F5EC" },
  reopened:  { label: "Reopened",         color: "#CC3A2E", bg: "#FDE9E7" },
};

const PRIO_COLOR = { high: "#E0453A", med: "#E8912B", low: "#3E9C6B" };

const STATUS_TABS = [
  { label: "All",        value: ""          },
  { label: "New",        value: "submitted" },
  { label: "Reviewing",  value: "review"    },
  { label: "District",   value: "district"  },
  { label: "In progress",value: "progress"  },
  { label: "Resolved",   value: "resolved"  },
  { label: "Reopened",   value: "reopened"  },
];

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: T.ink2, bg: T.bg };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: s.bg, color: s.color, borderRadius: 999, padding: "3px 10px", fontWeight: 700, fontSize: 12 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, display: "inline-block", flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function SlaChip({ slaHrsLeft }) {
  if (slaHrsLeft == null) return null;
  const urgent  = slaHrsLeft < 4;
  const warning = slaHrsLeft < 8;
  const color   = urgent ? "#E0453A" : warning ? "#E8912B" : "#12885A";
  const bg      = urgent ? "#FDE9E7" : warning ? "#FEF3E0" : "#E3F5EC";
  const label   = slaHrsLeft <= 0 ? "SLA breached" : `${Math.round(slaHrsLeft)}h left`;
  return (
    <span style={{ background: bg, color, borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function SupportInbox({ sdTickets, sdLoading, getSdTickets, districts, getDistricts, downloadSdReport }) {
  const history = useHistory();
  const [tab,    setTab]    = useState(0);
  const [search, setSearch] = useState("");
  const [page,   setPage]   = useState(1);
  const [districtFilter, setDistrictFilter] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [stats, setStats] = useState(null);

  const PAGE_SIZE = 30;
  const statusFilter = STATUS_TABS[tab].value;

  const filterParams = {
    statusFilter,
    search,
    district: districtFilter?.name || "",
    categoryKey: categoryFilter?.key || "",
  };

  // Stats span every page (KPI cards + pagination); status tab does not affect them.
  const loadStats = () => {
    axios.get("/api/support-desk/tickets/stats", {
      params: {
        search,
        district: districtFilter?.name || "",
        categoryKey: categoryFilter?.key || "",
      },
    })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!districts?.length) getDistricts();
    axios.get("/api/support-desk/categories")
      .then(({ data }) => setCategories(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getSdTickets({ ...filterParams, page });
  }, [tab, page, districtFilter, categoryFilter]);

  // Refresh counts when the non-status filters change (not on tab/page).
  useEffect(() => {
    loadStats();
  }, [districtFilter, categoryFilter]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setPage(1);
      getSdTickets({ ...filterParams, page: 1 });
      loadStats();
    }
  };

  // How many tickets match the CURRENT view (status tab + filters) — drives pagination.
  const viewTotal = stats
    ? (statusFilter ? (stats.byStatus?.[statusFilter] || 0) : stats.total)
    : sdTickets.length;
  const totalPages = Math.max(1, Math.ceil(viewTotal / PAGE_SIZE));

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "80vh" }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: T.orangeGrad, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <HelpOutlineIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: T.navy }}>Support Desk Inbox</Typography>
          <Typography variant="body2" color="text.secondary">Manage and respond to teacher support requests</Typography>
        </Box>
      </Box>

      {/* KPI row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 2, mb: 3 }}>
        {[
          { label: "Total tickets",  n: stats?.total     ?? "—",  color: T.navy,    iconBg: "#E8EDF5", Icon: AllInboxIcon          },
          { label: "Open",           n: stats?.open      ?? "—",  color: "#1673C4", iconBg: "#E4F2FF", Icon: PendingActionsIcon     },
          { label: "SLA at risk",    n: stats?.slaAtRisk ?? "—",  color: "#E0453A", iconBg: "#FDE9E7", Icon: WarningAmberIcon       },
          { label: "Resolved",       n: stats?.resolved  ?? "—",  color: "#12885A", iconBg: "#E3F5EC", Icon: CheckCircleOutlineIcon  },
        ].map(k => (
          <Box key={k.label} sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "13px", background: k.iconBg, color: k.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <k.Icon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.n}</Typography>
              <Typography sx={{ fontSize: 12, color: T.ink2, mt: .25 }}>{k.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Search + filters + report */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField
          size="small" placeholder="Search by ref, subject, teacher name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearch}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: "1 1 280px", maxWidth: 420 }}
        />
        <Autocomplete
          size="small"
          value={categoryFilter}
          options={categories || []}
          getOptionLabel={(o) => o.name || ""}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          onChange={(e, v) => { setCategoryFilter(v || null); setPage(1); }}
          renderOption={(props, o) => <Box component="li" {...props} key={o.key}>{o.name}</Box>}
          renderInput={(params) => <TextField {...params} label="Filter by category" />}
          sx={{ flex: "1 1 220px", maxWidth: 260 }}
        />
        <Autocomplete
          size="small"
          value={districtFilter}
          options={districts || []}
          getOptionLabel={(o) => o.name || ""}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(e, v) => { setDistrictFilter(v || null); setPage(1); }}
          renderOption={(props, o) => <Box component="li" {...props} key={o.id}>{o.name}</Box>}
          renderInput={(params) => <TextField {...params} label="Filter by district" />}
          sx={{ flex: "1 1 220px", maxWidth: 260 }}
        />
        <Button
          variant="outlined"
          startIcon={<VpnKeyIcon />}
          onClick={() => history.push("/in/support-desk/otp-lookup")}
          sx={{ ml: "auto", whiteSpace: "nowrap" }}
        >
          OTP Lookup
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => downloadSdReport(filterParams)}
          disabled={sdLoading}
          sx={{ whiteSpace: "nowrap" }}
        >
          Download Excel
        </Button>
      </Box>

      {/* Status tabs */}
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1); }} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
        {STATUS_TABS.map((s) => {
          const n = stats ? (s.value ? (stats.byStatus?.[s.value] || 0) : stats.total) : null;
          return <Tab key={s.value} label={n == null ? s.label : `${s.label} (${n})`} sx={{ fontWeight: 700, fontSize: 12 }} />;
        })}
      </Tabs>

      {sdLoading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Ticket list */}
      <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", overflow: "hidden" }}>
        {sdTickets.length === 0 && !sdLoading && (
          <Box sx={{ p: 5, textAlign: "center", color: T.ink3 }}>
            <HelpOutlineIcon sx={{ fontSize: 36, mb: 1, opacity: .4 }} />
            <Typography>No tickets found.</Typography>
          </Box>
        )}
        {sdTickets.map((t, i) => (
          <React.Fragment key={t.id}>
            {i > 0 && <Divider />}
            <Box
              onClick={() => history.push(`/in/support-desk/ticket/${t.id}`)}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center", gap: 2, p: 2, cursor: "pointer",
                "&:hover": { background: "#f9fafb" },
              }}
            >
              {/* Priority bar */}
              <Box sx={{ width: 4, height: 40, borderRadius: 4, background: PRIO_COLOR[t.priorityKey] || T.ink3, flexShrink: 0 }} />

              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: .5 }}>
                  <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: T.ink3 }}>{t.ref}</Typography>
                  <StatusPill status={t.status} />
                  <SlaChip slaHrsLeft={t.slaHrsLeft} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.subject}
                </Typography>
                <Typography sx={{ fontSize: 12, color: T.ink2 }}>
                  {t.teacherName} · {t.teacherSchool || t.teacherDistrict || ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: .5, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                  {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ""}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
          <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Typography sx={{ alignSelf: "center" }}>Page {page} of {totalPages}</Typography>
          <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </Box>
      )}
    </Box>
  );
}

const mapStateToProps = ({ sdTickets, sdLoading, districts }) => ({ sdTickets, sdLoading, districts });
export default connect(mapStateToProps, { getSdTickets, getDistricts, downloadSdReport })(SupportInbox);
